import React, { useState } from 'react';
import { Icons } from '../ui/Icons';
import Modal from '../ui/Modal';
import { MaintenanceLocationType, MaintenancePriority, MaintenanceRequest, StaffMember } from '../../types';
import { database } from '../../services/database';
import { toast } from 'react-hot-toast';

interface MaintenanceRequestModalProps {
    onClose: () => void;
    staffName: string;
    staffList: StaffMember[];
    existingRequests: MaintenanceRequest[];
}

const MaintenanceRequestModal: React.FC<MaintenanceRequestModalProps> = ({ onClose, staffName, staffList, existingRequests }) => {
    const [type, setType] = useState<MaintenanceLocationType>('Room');
    const [roomNumber, setRoomNumber] = useState('');
    const [facility, setFacility] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<MaintenancePriority>('Medium');
    const [reportedBy, setReportedBy] = useState(''); // New state for reporting staff
    const [isSubmitting, setIsSubmitting] = useState(false);

    const generateRequestId = () => {
        const count = existingRequests.length + 1;
        return `MR-${count.toString().padStart(4, '0')}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!reportedBy.trim()) {
            toast.error('Your name is required');
            return;
        }
        if (type === 'Room' && !roomNumber.trim()) {
            toast.error('Room number is required');
            return;
        }
        if (type === 'Facility' && !facility.trim()) {
            toast.error('Facility name is required');
            return;
        }
        if (!title.trim()) {
            toast.error('Issue title is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const requestId = generateRequestId();
            const newRequest: Partial<MaintenanceRequest> = {
                requestId,
                type,
                roomNumber: type === 'Room' ? roomNumber : undefined,
                facility: type === 'Facility' ? facility : undefined,
                title,
                description,
                priority,
                status: 'Open',
                createdBy: reportedBy, // Use the entered name
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                history: [
                    {
                        action: 'Created',
                        status: 'Open',
                        changedBy: reportedBy,
                        timestamp: new Date().toISOString(),
                        note: 'Initial report'
                    }
                ]
            };

            await database.createMaintenanceRequest(newRequest);
            toast.success('Maintenance request created');
            onClose();
        } catch (error) {
            console.error('Error creating maintenance request:', error);
            toast.error('Failed to create request');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} title="New Request" maxWidthClass="sm:max-w-xl">
            <form onSubmit={handleSubmit} className="flex flex-col">
                {/* Modal Header Subtitle - Inside the content area to match modern flow */}
                <div className="px-8 pb-4 pt-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Setup maintenance details before saving</p>
                </div>

                <div className="px-8 pb-8 space-y-6">
                    {/* Reporting Staff Selection */}
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Your Name (Mandatory)</label>
                        <div className="relative group">
                            <Icons.User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                            <select
                                value={reportedBy}
                                onChange={(e) => setReportedBy(e.target.value)}
                                required
                                className="w-full bg-indigo-50/50 dark:bg-slate-900 border-none rounded-2xl p-4 pl-12 text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer"
                            >
                                <option value="">Select your name...</option>
                                {staffList.sort((a, b) => a.name.localeCompare(b.name)).map(staff => (
                                    <option key={staff.email} value={staff.name}>{staff.name}</option>
                                ))}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                <Icons.ChevronDown className="w-4 h-4 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Location Type Toggles */}
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Location Type</label>
                        <div className="flex gap-2 p-1.5 bg-gray-50 dark:bg-slate-900 rounded-2xl">
                            {(['Room', 'Facility'] as MaintenanceLocationType[]).map((locType) => (
                                <button
                                    key={locType}
                                    type="button"
                                    onClick={() => setType(locType)}
                                    className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                        type === locType
                                            ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                                    }`}
                                >
                                    {locType}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Conditional Location Input */}
                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                        {type === 'Room' ? (
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Room Number</label>
                                <div className="relative group">
                                    <Icons.Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                                    <input
                                        type="text"
                                        value={roomNumber}
                                        onChange={(e) => setRoomNumber(e.target.value)}
                                        placeholder="e.g. 213"
                                        required
                                        className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 pl-12 text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-gray-300 dark:placeholder:text-slate-700"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Facility Name</label>
                                <div className="relative group">
                                    <Icons.Home className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                                    <input
                                        type="text"
                                        value={facility}
                                        onChange={(e) => setFacility(e.target.value)}
                                        placeholder="e.g. Lobby, Gym, Pool"
                                        required
                                        className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 pl-12 text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-gray-300 dark:placeholder:text-slate-700"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Priority Selector */}
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Priority Level</label>
                        <div className="grid grid-cols-4 gap-2">
                            {(['Low', 'Medium', 'High', 'Urgent'] as MaintenancePriority[]).map((p) => {
                                const activeStyles: any = {
                                    Low: 'bg-green-500 text-white shadow-lg shadow-green-500/20',
                                    Medium: 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/20',
                                    High: 'bg-orange-500 text-white shadow-lg shadow-orange-500/20',
                                    Urgent: 'bg-red-600 text-white shadow-lg shadow-red-600/20',
                                };
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setPriority(p)}
                                        className={`py-3 px-1 text-center rounded-xl text-[10px] font-black uppercase tracking-tight transition-all ${
                                            priority === p
                                                ? activeStyles[p]
                                                : 'bg-gray-50 dark:bg-slate-900 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Title and Description */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Issue Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="What needs to be fixed?"
                                required
                                className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-gray-300 dark:placeholder:text-slate-700"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Fault Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Describe the problem in detail..."
                                rows={3}
                                className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all resize-none placeholder:text-gray-300 dark:placeholder:text-slate-700"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-8 bg-gray-50/50 dark:bg-slate-900/40 border-t border-gray-100 dark:border-slate-800 flex gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-4 bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 font-black rounded-2xl border border-gray-100 dark:border-slate-700 hover:bg-gray-50 transition-all uppercase tracking-widest text-[11px]"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-[2] py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none uppercase tracking-widest text-[11px] flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Icons.Loader className="w-4 h-4 animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Icons.PlusCircle className="w-4 h-4" />
                                <span>Initialize Request</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default MaintenanceRequestModal;
